import Section from 'components/Section'
import { MediaImageStandard, Typography, TypographyLongForm } from 'components/Contentful'
import { Eyebrow } from 'components/Eyebrow'
import { SocialMediaProfile } from 'components/SocialMediaProfile'
import { AuthorBreadCrumbs } from 'components/AuthorBreadCrumbs'
import { Box } from '@achieve/ascend'
import { AuthorLinks } from 'components/AuthorLinks'
import { CloserStatementSection } from 'pageSections/CloserStatementSection'
import { AuthorArticlesPaginated } from 'components/AuthorArticlesPaginated'
import CollapseByHeight from 'components/CollapseByHeight/CollapseByHeight'
import { rewriteAssetUrl } from 'utils/conversions'
import { AUTHORS_ROUTE } from 'constants/navigation'
import { useContext } from 'react'
import { LayoutContext } from 'providers/LayoutProvider'

const AuthorRender = ({ pageContent, completePageUrl, pageHost }) => {
  const { state: { siteInfo } = {} } = useContext(LayoutContext)
  if (!pageContent?.content) {
    return null
  }

  const { closerStatement, author, articlesAuthor } = pageContent.content

  if (!author) {
    return null
  }

  const {
    authorPhoto,
    contentRole,
    authorName,
    authorPageConfig,
    facebookProfile,
    twitterProfile,
    linkedInProfile,
    authorFullBio,
    externalLinks,
  } = author.fields

  const authorSchema = {
    '@context': 'https://schema.org/',
    '@type': ['ProfilePage'],
    '@id': `https://${pageHost}${AUTHORS_ROUTE}/${author?.fields?.slug}`,
    mainEntity: {
      '@type': 'Person',
      name: authorName,
      jobTitle: 'Senior Writer',
      description: authorFullBio?.content?.[0]?.content?.[0]?.value,
      url: `https://${pageHost}${AUTHORS_ROUTE}/${author?.fields?.slug}`,
      image: `${pageHost}${rewriteAssetUrl(authorPhoto?.fields?.file?.url)}`,
      worksFor: [
        {
          '@type': 'Organization',
          name: siteInfo?.mainName,
        },
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo?.mainName,
      url: siteInfo?.publicUrl,
      logo: {
        '@type': 'ImageObject',
        url: siteInfo?.logoUrl,
        width: 300,
        height: 74,
      },
      brand: siteInfo?.mainName,
      publishingPrinciples: `${siteInfo?.publicUrl}/about`,
      sameAs: siteInfo?.socialMediaLinks,
    },
  }

  return (
    <>
      <Section className={''} data-testid="AuthorRender_section">
        <div className={'mb-2 [&_a]:[&_p]:text-xs md:mb-8'}>
          <AuthorBreadCrumbs authorName={authorName} />
        </div>
        <div className={'flex flex-col md:flex-row'}>
          <div className={'flex'}>
            <div
              className={
                'w-full mb-6 [&>span]:max-h-full [&>span]:max-w-full [&>span]:[&>img]:!min-h-full [&>span]:[&>img]:!max-h-full md:h-[190px] md:w-[180px] md:mb-4 md:[&>span]:[&>img]:!min-h-full md:[&>span]:[&>img]:!max-h-full'
              }
            >
              <MediaImageStandard
                style={{ borderRadius: '10px' }}
                alt={`author ${authorName} profile photo`}
                aspectRatio={1 / 1}
                content={authorPhoto}
              />
            </div>
          </div>
          <div
            className={
              'block flex-col w-full mb-8 md:font-normal md:px-8 md:py-0 md:text-[15px] md:leading-[170%]'
            }
            data-testid="AuthorRender_content"
          >
            <Eyebrow
              black
              content={contentRole?.join()}
              data-testid="author-eyebrow"
              className="!text-left"
            />
            <Typography
              component="h1"
              content={authorName}
              className="displayMd lg:displayLg font-normal"
            />
            <div className={'m-0 md:mx-0 md:my-4'} data-testid="AuthorRender_socialcontent">
              <SocialMediaProfile
                facebook={facebookProfile}
                twitter={twitterProfile}
                linkedin={linkedInProfile}
              />
            </div>
            {authorPageConfig?.maxHeightMobile && (
              <CollapseByHeight
                collapseHeight={authorPageConfig.maxHeightMobile}
                className="peer block lg:hidden"
              >
                <TypographyLongForm content={authorFullBio} data-testid="author-bio-content" />
              </CollapseByHeight>
            )}
            <div className="peer-data-[testid='collapse-by-height']:hidden lg:!block">
              <TypographyLongForm content={authorFullBio} />
            </div>
          </div>
          {externalLinks && (
            <>
              <Box
                className={'min-w-[320px] h-fit mb-8 p-0 md:bg-blue-25 md:rounded-[10px] md:p-8'}
                data-testid="related-articles-container"
              >
                <hr
                  className={'mx-0 my-8 border-t-neutral-150 border-t border-solid block lg:hidden'}
                />
                <AuthorLinks
                  data-testid="posts-related-articles"
                  links={externalLinks}
                  completePageUrl={completePageUrl}
                  title={
                    <Typography
                      className={'mb-8 headingMd lg:bodyLg font-normal'}
                      content={'Other publications'}
                      data-testid="related-articles-title"
                    />
                  }
                  buttons={'Read more'}
                />
              </Box>
            </>
          )}
        </div>
      </Section>
      {articlesAuthor?.totalArticles > 0 && (
        <AuthorArticlesPaginated
          authorName={authorName}
          className={'flex bg-blue-25'}
          articlesAuthor={articlesAuthor}
        />
      )}
      {closerStatement.items[0] && (
        <CloserStatementSection
          data-testid="post-closer-statement"
          content={closerStatement.items[0]}
        />
      )}
      <script
        id="author-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(authorSchema) }}
      />
    </>
  )
}

export { AuthorRender }
